import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import "./../styles/Signin.css";
import { MyContext } from "../MyContextApi";
import { Tooltip } from "@mui/material";
import api from "../API/axiosInstance";

const AUTO_REFRESH_MS = 60000;
const PROGRESS_UPDATE_MS = 100;
const EXTERNAL_TRIGGER_COOLDOWN_MS = 1500;

// Use axios (api) instead of raw fetch — sends cookies automatically
const generateCaptcha = async () => {
  try {
    const response = await api.get("/captcha2/generate");
    const data = response.data;
    return data?.captchaImage && data?.captchaId
      ? { captchaImage: data.captchaImage, captchaId: data.captchaId }
      : null;
  } catch (error) {
    console.error("Error generating captcha:", error);
    return null;
  }
};

const SimpleCaptcha = ({ onCaptchaChange, refreshTrigger }) => {
  const { captchaData, setCaptchaData } = useContext(MyContext);

  const mountedRef = useRef(false);
  const timeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const endTimeRef = useRef(null);
  const lastHandledExternalRef = useRef(null);
  const externalCooldownRef = useRef(false);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [progressPct, setProgressPct] = useState(0);

  const refreshCaptcha = useCallback(async () => {
    try {
      const newCaptchaData = await generateCaptcha();
      if (!mountedRef.current) return null;
      if (newCaptchaData) {
        setCaptchaData(newCaptchaData);
      }
      return newCaptchaData;
    } catch (err) {
      console.error("Captcha refresh failed:", err);
      return null;
    }
  }, [setCaptchaData]);

  const clearSchedule = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    endTimeRef.current = null;
    setProgressPct(0);
  }, []);

  const startProgressUpdates = useCallback((durationMs = AUTO_REFRESH_MS) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    const start = Date.now();
    endTimeRef.current = start + durationMs;

    const tick = () => {
      if (!endTimeRef.current) return;
      const now = Date.now();
      const elapsed = now - (endTimeRef.current - durationMs);
      const pct = Math.min(100, Math.max(0, (elapsed / durationMs) * 100));
      setProgressPct(pct);
      if (now >= endTimeRef.current) setProgressPct(100);
    };

    tick();
    progressIntervalRef.current = setInterval(tick, PROGRESS_UPDATE_MS);
  }, []);

  const scheduleNext = useCallback(
    (delay = AUTO_REFRESH_MS) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      startProgressUpdates(delay);
      timeoutRef.current = setTimeout(async () => {
        setIsRefreshing(true);
        await refreshCaptcha();
        setIsRefreshing(false);
        if (mountedRef.current) scheduleNext(AUTO_REFRESH_MS);
      }, delay);
    },
    [refreshCaptcha, startProgressUpdates],
  );

  const refreshAndReset = useCallback(
    async (opts = { externalTriggerValue: undefined }) => {
      if (!mountedRef.current || isRefreshing) return;

      if (opts.externalTriggerValue !== undefined) {
        lastHandledExternalRef.current = opts.externalTriggerValue;
        externalCooldownRef.current = true;
        setTimeout(() => {
          externalCooldownRef.current = false;
        }, EXTERNAL_TRIGGER_COOLDOWN_MS);
      }

      clearSchedule();
      setIsRefreshing(true);
      await refreshCaptcha();
      setIsRefreshing(false);
      if (mountedRef.current) scheduleNext(AUTO_REFRESH_MS);
    },
    [clearSchedule, refreshCaptcha, scheduleNext, isRefreshing],
  );

  // initial load
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      setIsRefreshing(true);
      await refreshCaptcha();
      setIsRefreshing(false);
      if (mountedRef.current) scheduleNext(AUTO_REFRESH_MS);
    })();
    return () => {
      mountedRef.current = false;
      clearSchedule();
    };
  }, [refreshCaptcha, scheduleNext, clearSchedule]);

  // notify parent when captchaData changes
  useEffect(() => {
    if (typeof onCaptchaChange === "function" && captchaData) {
      onCaptchaChange(captchaData);
    }
  }, [captchaData, onCaptchaChange]);

  // external trigger handling
  useEffect(() => {
    if (refreshTrigger === undefined || refreshTrigger === null) return;
    if (lastHandledExternalRef.current === refreshTrigger) return;
    if (externalCooldownRef.current) return;
    refreshAndReset({ externalTriggerValue: refreshTrigger });
  }, [refreshTrigger, refreshAndReset]);

  const remainingSeconds =
    endTimeRef.current && Date.now() < endTimeRef.current
      ? Math.ceil((endTimeRef.current - Date.now()) / 1000)
      : Math.ceil(AUTO_REFRESH_MS / 1000);

  return (
    <div className="captcha-wrapper">
      <div className="captcha-container">
        <img
          src={captchaData?.captchaImage || ""}
          alt="captcha"
          width="180"
          height="60"
          className="captcha-canvas"
          style={{ display: "block" }}
        />

        <div className="captcha-progress-wrapper">
          <div
            className="captcha-progress-bar"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="captcha-refresh-wrapper">
          <Tooltip
            title={
              isRefreshing ? "Refreshing…" : `Reset (${remainingSeconds}s)`
            }
            arrow
          >
            <button
              type="button"
              className="captcha-refresh-button"
              onClick={() => refreshAndReset()}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <svg
                  className="captcha-spinner"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.48" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M23 4v6h-6" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
              )}
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default SimpleCaptcha;
